import type { NotificationItemProps } from './Notifications.types';
import type { ReactElement } from 'react';

import { Box, Flex, Text } from '@rango-dev/whale';
import { EventSeverity, useWidget } from '@rango-dev/widget-embedded';
import clsx from 'clsx';
import { ChevronRight } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '../../hooks/reduxHooks';
import { getBlockchainLogo } from '../../utils/Constants';

import { NotificationChainToken } from './Notifications.ChainToken';
import styles from './Notifications.module.css';

const CHEVRON_SIZE = 20;

export function NotificationItem(props: NotificationItemProps): ReactElement {
  const { notification, onSelect } = props;
  const { t } = useTranslation();
  const { findToken } = useWidget().meta;
  const blockchains = useAppSelector((state) => state.meta.blockchains);

  const { route, event, requestId } = notification;
  const isError = event.messageSeverity === EventSeverity.ERROR;
  const itemId = `notification-item-${requestId}`;
  /*
   * `event.message` is generic status copy ("Executing transaction …") that never names
   * the swap, so the token/chain logos are the only place its identity is exposed. Label
   * the pair as one composite image and keep the individual `<img>`s decorative, so a
   * screen reader announces the route once instead of four unlabeled logos.
   */
  const routeLabel = t('{{fromSymbol}} on {{fromBlockchain}} to {{toSymbol}} on {{toBlockchain}}', {
    fromSymbol: route.from.symbol,
    fromBlockchain: route.from.blockchain,
    toSymbol: route.to.symbol,
    toBlockchain: route.to.blockchain,
  });

  return (
    <button
      type="button"
      id={itemId}
      className={clsx(styles.item, isError && styles.itemError)}
      onClick={() => onSelect(requestId)}>
      <span className={styles.chainTokenPair} role="img" aria-label={routeLabel}>
        <NotificationChainToken
          tokenImage={findToken(route.from)?.image}
          chainImage={getBlockchainLogo(route.from.blockchain, blockchains)}
        />
        <NotificationChainToken
          tokenImage={findToken(route.to)?.image}
          chainImage={getBlockchainLogo(route.to.blockchain, blockchains)}
        />
      </span>

      <Flex align="center" gap="2" flexGrow="1" minWidth="0">
        <Box flexGrow="1" minWidth="0">
          <Text size="2">{t(event.message)}</Text>
        </Box>
        <span className={styles.itemChevron}>
          <ChevronRight size={CHEVRON_SIZE} />
        </span>
      </Flex>
    </button>
  );
}
